import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { API, storage } from '../lib/utils';
import { toast } from 'sonner';
import {
  MessageSquarePlus,
  Bug,
  Lightbulb,
  HelpCircle,
  Loader2,
  ChevronRight,
  Paperclip,
  X,
} from 'lucide-react';
import axios from 'axios';

const TYPE_CONFIG = {
  bug: { label: 'Bug Report', icon: Bug, color: 'text-red-600 bg-red-50 border-red-200' },
  feature: { label: 'Feature Request', icon: Lightbulb, color: 'text-amber-600 bg-amber-50 border-amber-200' },
  other: { label: 'Other', icon: HelpCircle, color: 'text-slate-600 bg-slate-50 border-slate-200' },
};

const STATUS_STYLES = {
  open: 'bg-blue-50 text-blue-700 border-blue-200',
  in_progress: 'bg-amber-50 text-amber-700 border-amber-200',
  resolved: 'bg-green-50 text-green-700 border-green-200',
  closed: 'bg-slate-100 text-slate-500 border-slate-200',
};

const StatusLabel = {
  open: 'Open',
  in_progress: 'In Progress',
  resolved: 'Resolved',
  closed: 'Closed',
};

const FeedbackPage = () => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [type, setType] = useState('bug');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState([]);

  const headers = useCallback(() => {
    const token = storage.get('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const loadTickets = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/tickets`, { headers: headers() });
      setTickets(res.data);
    } catch {
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => { loadTickets(); }, [loadTickets]);

  const handleFileChange = (e) => {
    const selected = Array.from(e.target.files || []);
    const maxSize = 5 * 1024 * 1024; // 5MB
    const valid = selected.filter(f => {
      if (f.size > maxSize) { toast.error(`${f.name} exceeds 5MB limit`); return false; }
      return true;
    });
    setFiles(prev => [...prev, ...valid]);
  };

  const filesToAttachments = async (fileList) => {
    return Promise.all(fileList.map(f => new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve({
        filename: f.name,
        content_type: f.type,
        data: reader.result.split(',')[1],
      });
      reader.readAsDataURL(f);
    })));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !message.trim()) return;
    setSubmitting(true);
    try {
      const attachments = await filesToAttachments(files);
      await axios.post(`${API}/tickets`, { type, subject, message, attachments }, { headers: headers() });
      toast.success('Feedback submitted!');
      setShowForm(false);
      setSubject(''); setMessage(''); setFiles([]); setType('bug');
      loadTickets();
    } catch {
      toast.error('Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-64px)]">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="feedback-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Feedback</h1>
          <p className="text-muted-foreground mt-1">Report bugs or request features</p>
        </div>
        <Button
          data-testid="new-ticket-btn"
          onClick={() => setShowForm(true)}
          className="gap-2"
        >
          <MessageSquarePlus className="w-4 h-4" />
          New Ticket
        </Button>
      </div>

      {/* New ticket form */}
      {showForm && (
        <Card data-testid="new-ticket-form">
          <CardHeader>
            <CardTitle className="text-lg">Submit Feedback</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Type selector */}
              <div className="flex gap-2">
                {Object.entries(TYPE_CONFIG).map(([key, cfg]) => (
                  <button
                    key={key}
                    type="button"
                    data-testid={`type-${key}`}
                    onClick={() => setType(key)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                      type === key ? cfg.color + ' ring-2 ring-offset-1 ring-current' : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    <cfg.icon className="w-4 h-4" />
                    {cfg.label}
                  </button>
                ))}
              </div>

              <Input
                data-testid="ticket-subject"
                placeholder="Subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                required
              />

              <Textarea
                data-testid="ticket-message"
                placeholder="Describe the issue or feature you'd like..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
                required
              />

              {/* File attachments */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer hover:text-foreground transition-colors w-fit">
                  <Paperclip className="w-4 h-4" />
                  Attach files (max 5MB each)
                  <input
                    data-testid="ticket-file-input"
                    type="file"
                    multiple
                    accept="image/*,.pdf,.txt,.log"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </label>
                {files.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {files.map((f, i) => (
                      <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 rounded text-xs font-medium">
                        {f.name}
                        <button type="button" onClick={() => setFiles(prev => prev.filter((_, idx) => idx !== i))}>
                          <X className="w-3 h-3 text-slate-400 hover:text-slate-700" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex gap-2 justify-end">
                <Button type="button" variant="ghost" onClick={() => { setShowForm(false); setFiles([]); }}>
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting} data-testid="submit-ticket-btn" className="gap-2">
                  {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                  Submit
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Ticket list */}
      {tickets.length === 0 && !showForm ? (
        <Card>
          <CardContent className="py-16 text-center">
            <MessageSquarePlus className="w-12 h-12 mx-auto mb-4 text-muted-foreground/40" />
            <p className="text-muted-foreground">No tickets yet. Click "New Ticket" to get started.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {tickets.map((t) => {
            const cfg = TYPE_CONFIG[t.type] || TYPE_CONFIG.other;
            return (
              <Card
                key={t.id}
                data-testid={`ticket-row-${t.id}`}
                className="cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => navigate(`/feedback/${t.id}`)}
              >
                <CardContent className="py-4 flex items-center gap-4">
                  <div className={`shrink-0 w-9 h-9 rounded-lg flex items-center justify-center border ${cfg.color}`}>
                    <cfg.icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{t.subject}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {formatTime(t.created_at)}
                    </p>
                  </div>
                  <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${STATUS_STYLES[t.status] || ''}`}>
                    {StatusLabel[t.status] || t.status}
                  </span>
                  <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FeedbackPage;
